import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useTable } from "react-table";
import { COLUMNS } from "./DemoAccColumns";
import { FaMinusCircle, FaPlusCircle } from "react-icons/fa";
import { showToast } from "../../../../../utils/utils";
import PropTypes from "prop-types";
import FundedDatePicker from "../../../../datepicker/FundedDatePicker";
import publicAxios from "../../../../../utils/publicAxios";
import "./fundinvtable.scss";
import {
  fundedInvoicesData,
  profiletexts,
  responseStatus
} from "../../../../../locales/en";
import { getDemoInvoiceByIdsURL } from "../../../../../utils/constants/apiEndPoints";

const CURRENCY_ACCESSORS = ["advanceAmount", "feeAmount"];

// Blank amounts show as $0.00 rather than an empty cell
const formatAmount = (value) => {
  if (value === undefined || value === null || value === "") {
    return "$0.00";
  }
  return `$${value.toFixed(2)}`;
};

const DemoFundInvTable = (props) => {
  const { selectInvoices } = props;
  const [expandedRows, setExpandedRows] = useState([]);
  const [quotedData, setQuotedData] = useState([]);
  const columns = useMemo(() => COLUMNS, []);
  const userData = localStorage.getItem("fundTap_demoUser_account_setup");
  const parsedData = JSON.parse(userData);
  const userId = parsedData?.id;

  // for quoted data
  const loadInvoiceQuoteData = useCallback(async () => {
    try {
      const selectedInvoicesRequestBody = [];
      selectInvoices.forEach((invoice) => {
        selectedInvoicesRequestBody.push({
          demoInvoiceId: invoice.id,
          demoInvoiceRepaymentDate: invoice.dueDate
        });
      });
      const response = await publicAxios.post(getDemoInvoiceByIdsURL(userId), {
        demoInvoiceDetails: selectedInvoicesRequestBody
      });
      const responseData = response.data.data;

      // Add a check to ensure responseData and responseData.invoices are defined
      if (responseData && responseData.invoices) {
        setQuotedData(responseData.invoices);
      } else {
        console.error(profiletexts.errorMessage.invoiceResponseError);
      }
    } catch {
      // handle catch here;
    }
  }, [selectInvoices, userId]);

  useEffect(() => {
    loadInvoiceQuoteData();
  }, [loadInvoiceQuoteData]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data: quotedData
    });

  const toggleRow = useCallback((rowId) => {
    setExpandedRows((prevState) =>
      prevState.includes(rowId)
        ? prevState.filter((id) => id !== rowId)
        : [...prevState, rowId]
    );
  }, []);

  // Reayment Date Change Function Start
  const [showNewDateButton, setShowNewDateButton] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const handleRepayDate = async (date, rowId) => {
    const formattedDate = date ? date.toLocaleDateString("en-CA") : "";

    setSelectedInvoiceId(rowId);
    setSelectedDate(formattedDate);
    setShowNewDateButton((prevState) => ({ ...prevState, [rowId]: true }));
  };

  const handleNewDateButtonClick = async () => {
    if (!selectedInvoiceId || !selectedDate) return;

    try {
      const dataToSend = {
        demoInvoiceDetails: selectInvoices.map((invoice) => ({
          demoInvoiceId: invoice.id,
          demoInvoiceRepaymentDate:
            invoice.id === selectedInvoiceId ? selectedDate : invoice.dueDate
        }))
      };
      const response = await publicAxios.post(
        getDemoInvoiceByIdsURL(userId),
        dataToSend
      );

      if (response.status === 200) {
        setShowNewDateButton(false);
        setSelectedInvoiceId(null);
        setSelectedDate(null);
        const responseData = response.data.data;
        setQuotedData(responseData.invoices);
      }
    } catch (error) {
      showToast(error.response.data.message, responseStatus.error);
    }
  };

  return (
    <table className="mb-3 fundinvdemotable" {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup) => {
          const { key, ...rest } = headerGroup.getHeaderGroupProps();
          return (
            <tr className="parentrow" {...rest} key={key}>
              <th />
              {headerGroup.headers.map((column) => (
                <th key={column.id}>
                  <span>{column.render("Header")}</span>
                </th>
              ))}
            </tr>
          );
        })}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);
          const isExpanded = expandedRows.includes(row.id);
          return (
            <React.Fragment key={row.id}>
              <tr className="parentrow" {...row.getRowProps()}>
                <td className="expand-td">
                  <span
                    className="expandbtn cursor-pointer"
                    onClick={() => toggleRow(row.id)}
                  >
                    {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                  </span>
                </td>
                {row.cells.map((cell) => (
                  <td key={cell.getCellProps().key} {...cell.getCellProps()}>
                    {cell.column.id === "dueDate" ? (
                      <>
                        <FundedDatePicker
                          selected={new Date(cell.value)}
                          onChange={(date) =>
                            handleRepayDate(date, row.original.id)
                          }
                        />
                        {showNewDateButton[row.original.id] && (
                          <button
                            className="btn btn-primary new-pay-date mt-1"
                            onClick={handleNewDateButtonClick}
                          >
                            {fundedInvoicesData.funded.newDate}
                          </button>
                        )}
                      </>
                    ) : (
                      cell.render("Cell")
                    )}
                  </td>
                ))}
              </tr>

              {isExpanded && (
                <tr className="childrow colexpand">
                  <td colSpan={columns.length + 2}>
                    <div className="tdbody">
                      {columns.map((column) => {
                        return (
                          <span
                            key={column.accessor}
                            data-label={column.Header}
                          >
                            {CURRENCY_ACCESSORS.includes(column.accessor)
                              ? formatAmount(row.original[column.accessor])
                              : row.original[column.accessor]}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};
DemoFundInvTable.propTypes = {
  selectInvoices: PropTypes.array.isRequired
};
export default DemoFundInvTable;
